import { FormField, Input, SegmentedControl, Select, Text, Textarea } from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { errorMessage } from '../../../lib/errors';
import { useCustomers, useSaveCustomer } from '../../crm/api';
import {
  CustomerFields,
  customerErrors,
  useCustomerForm,
} from '../../crm/components/CustomerFields';
import { formatEuro } from '../format';

/** Invoices up to this gross amount need no recipient (§ 33 UStDV). */
export const SMALL_AMOUNT_CENTS = 25_000;

type Way = 'none' | 'customer' | 'new' | 'text';

export interface InvoiceRecipient {
  customer_id?: string;
  recipient?: { name: string; lines: string[] };
}

export interface InvoiceRecipientDialogProps {
  onClose: () => void;
  totalCents: number;
  pending: boolean;
  error: unknown;
  onSubmit: (recipient: InvoiceRecipient) => void;
  /**
   * Changing the recipient of a draft: no customer (the sale keeps none),
   * prefilled, saved instead of "create invoice".
   */
  current?: { name: string; lines: string[] } | null;
}

/** Recipient of an invoice for a sale without customer (market, MKT-017). */
export function InvoiceRecipientDialog({
  onClose,
  totalCents,
  pending,
  error,
  onSubmit,
  current,
}: InvoiceRecipientDialogProps) {
  const { t } = useTranslation();
  const editing = current !== undefined;
  const small = totalCents <= SMALL_AMOUNT_CENTS;
  const customers = useCustomers({}, !editing);
  const save = useSaveCustomer();
  const form = useCustomerForm();
  const [way, setWay] = useState<Way>(
    editing ? (current || !small ? 'text' : 'none') : small ? 'none' : 'customer',
  );
  const [customer, setCustomer] = useState('');
  // A customer created by an earlier, failed submit is reused, not created twice.
  const [created, setCreated] = useState<string | null>(null);
  const [name, setName] = useState(current?.name ?? '');
  const [address, setAddress] = useState(current?.lines.join('\n') ?? '');

  const lines = address
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const valid =
    way === 'none' ||
    way === 'new' ||
    (way === 'customer' && customer !== '') ||
    (way === 'text' && (small || (name.trim() !== '' && lines.length > 0)));
  const submit = () => {
    if (way === 'customer') onSubmit({ customer_id: customer });
    else if (way === 'new' && created) onSubmit({ customer_id: created });
    else if (way === 'new')
      // The new customer is created first, then used as recipient.
      save.mutate(
        { body: form.body() },
        {
          onSuccess: (c) => {
            setCreated(c.id);
            onSubmit({ customer_id: c.id });
          },
        },
      );
    else if (way === 'text') onSubmit({ recipient: { name: name.trim(), lines } });
    else onSubmit({});
  };
  const customerError = way === 'new' ? customerErrors(save.error) : undefined;
  const general = customerError?.general ?? (error ? errorMessage(error) : undefined);

  return (
    <FormDialog
      open
      onClose={onClose}
      title={editing ? t('sales.recipient.editTitle') : t('sales.recipient.title')}
      description={small ? t('sales.recipient.hintSmall') : t('sales.recipient.hintBig')}
      submitLabel={editing ? t('common.save') : t('sales.recipient.submit')}
      onSubmit={() => valid && submit()}
      pending={pending || save.isPending}
      error={general}
    >
      <SegmentedControl
        label={t('sales.recipient.way')}
        value={way}
        onChange={setWay}
        fullWidth
        options={[
          ...(small ? [{ value: 'none' as const, label: t('sales.recipient.none') }] : []),
          ...(editing
            ? []
            : [
                { value: 'customer' as const, label: t('sales.recipient.customer') },
                { value: 'new' as const, label: t('sales.recipient.new') },
              ]),
          { value: 'text' as const, label: t('sales.recipient.text') },
        ]}
      />
      {way === 'none' && (
        <Text size="sm" tone="muted">
          {t('sales.recipient.noneHint', { total: formatEuro(totalCents) })}
        </Text>
      )}
      {way === 'customer' && (
        <FormField label={t('sales.form.customer')} required>
          {(p) => (
            <Select
              {...p}
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              options={[
                { value: '', label: t('sales.form.chooseCustomer') },
                ...(customers.data ?? []).map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
          )}
        </FormField>
      )}
      {way === 'new' && customerError && (
        <CustomerFields form={form} errors={customerError} compact />
      )}
      {way === 'text' && (
        <>
          <FormField label={t('sales.recipient.name')} required={!small}>
            {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} />}
          </FormField>
          <FormField label={t('sales.recipient.address')} required={!small}>
            {(p) => (
              <Textarea
                {...p}
                rows={3}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            )}
          </FormField>
        </>
      )}
    </FormDialog>
  );
}
